import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Camera as CapCamera } from "@capacitor/camera";
import axios from "axios";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Camera, Keyboard, Upload } from "lucide-react";
import CameraCapture from "@/components/CameraCapture";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Scanner = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', status: 'safe', image_url: '', barcode: '' });
  const [imagePreview, setImagePreview] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        // Only a real auth failure sends us home; a transient backend error
        // shouldn't kick the user out of the scanner.
        if (error?.response?.status === 401) {
          navigate('/');
        } else {
          toast.error('Could not verify your session. Please try again.');
        }
      }
    };
    checkAuth();
  }, [navigate]);

  const startScanning = async () => {
    setScanning(true);
    try {
      // On native Capacitor, request camera permission via native API
      if (Capacitor.isNativePlatform()) {
        const permStatus = await CapCamera.requestPermissions({ permissions: ['camera'] });
        if (permStatus.camera !== 'granted') {
          toast.error('Camera permission denied. Please allow camera access in your device settings.');
          setScanning(false);
          return;
        }
      } else {
        // Web: check getUserMedia support
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          toast.error('Camera not supported on this device. Please use manual entry.');
          setScanning(false);
          return;
        }
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
          stream.getTracks().forEach(track => track.stop());
        } catch (permissionError) {
          if (permissionError.name === 'NotAllowedError' || permissionError.name === 'PermissionDeniedError') {
            toast.error('Camera permission denied. Please allow camera access in your browser settings.');
          } else if (permissionError.name === 'NotFoundError') {
            toast.error('No camera found on this device.');
          } else {
            toast.error('Unable to access camera: ' + permissionError.message);
          }
          setScanning(false);
          return;
        }
      }

      const html5QrCode = new Html5Qrcode("qr-reader");
      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        async (decodedText) => {
          await html5QrCode.stop();
          setScanning(false);
          await searchProduct(decodedText);
        },
        (errorMessage) => {
          console.log(errorMessage);
        }
      );
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Unable to access camera. Please use manual entry or check camera permissions in your browser settings.');
      setScanning(false);
    }
  };

  const searchProduct = async (barcode) => {
    try {
      const response = await axios.get(`${API}/products/search?barcode=${barcode}`);
      if (response.data.found) {
        navigate(`/product/${barcode}`);
      } else {
        setNewProduct({ ...newProduct, barcode });
        setShowAddForm(true);
        toast.info('Product not found. Add it to the database!');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Error searching for product');
    }
  };

  const handleManualSearch = async (e) => {
    e.preventDefault();
    if (manualBarcode.trim()) {
      await searchProduct(manualBarcode.trim());
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setNewProduct({ ...newProduct, image_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraCapture = (imageData) => {
    setImagePreview(imageData);
    setNewProduct({ ...newProduct, image_url: imageData });
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/products`, newProduct);
      toast.success('Product added successfully!');
      setShowAddForm(false);
      navigate(`/product/${newProduct.barcode}`);
      setNewProduct({ name: '', status: 'safe', image_url: '', barcode: '' });
      setImagePreview(null);
    } catch (error) {
      console.error('Add product error:', error);
      toast.error('Failed to add product');
    }
  };

  return (
    <div className="scanner-container">
      {showCamera && (
        <CameraCapture
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}

      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          style={{ marginBottom: '1rem' }}
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '2rem', textAlign: 'center' }} data-testid="scanner-title">
          Scan Product
        </h1>

        <Card style={{ marginBottom: '2rem' }}>
          <CardHeader>
            <CardTitle>Search for Product</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="manual">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="manual" data-testid="manual-tab">
                  <Keyboard className="w-4 h-4 mr-2" />
                  Manual Entry
                </TabsTrigger>
                <TabsTrigger value="camera" data-testid="camera-tab">
                  <Camera className="w-4 h-4 mr-2" />
                  Camera Scan
                </TabsTrigger>
              </TabsList>

              <TabsContent value="manual">
                <form onSubmit={handleManualSearch} style={{ marginTop: '1rem' }}>
                  <Label htmlFor="barcode">Enter Barcode</Label>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <Input
                      id="barcode"
                      placeholder="Enter barcode number"
                      value={manualBarcode}
                      onChange={(e) => setManualBarcode(e.target.value)}
                      data-testid="manual-barcode-input"
                    />
                    <Button type="submit" data-testid="search-button">Search</Button>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="camera">
                <div style={{ marginTop: '1rem' }}>
                  <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem', lineHeight: '1.5' }}>
                    📱 <strong>Note:</strong> Camera scanning works best on mobile devices. PC webcams may have difficulty reading barcodes due to fixed focus. Use manual entry if scanning doesn't work.
                  </p>
                  {!scanning ? (
                    <Button onClick={startScanning} className="w-full" data-testid="start-scan-button">
                      <Camera className="w-4 h-4 mr-2" />
                      Start Camera
                    </Button>
                  ) : (
                    <div>
                      <div id="qr-reader" data-testid="qr-reader"></div>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setScanning(false);
                          window.location.reload();
                        }}
                        className="w-full mt-2"
                        data-testid="stop-scan-button"
                      >
                        Stop Scanning
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {showAddForm && (
          <Card style={{ marginBottom: '2rem' }}>
            <CardHeader>
              <CardTitle>Add New Product</CardTitle>
              <CardDescription>Barcode: {newProduct.barcode}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddProduct} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <Label htmlFor="name">Product Name</Label>
                  <Input
                    id="name"
                    placeholder="Enter product name"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    required
                    data-testid="product-name-input"
                  />
                </div>
                <div>
                  <Label>Cilantro Status</Label>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <Button
                      type="button"
                      variant={newProduct.status === 'safe' ? 'default' : 'outline'}
                      onClick={() => setNewProduct({ ...newProduct, status: 'safe' })}
                      data-testid="status-safe-button"
                    >
                      ✓ Safe
                    </Button>
                    <Button
                      type="button"
                      variant={newProduct.status === 'poisoned' ? 'default' : 'outline'}
                      onClick={() => setNewProduct({ ...newProduct, status: 'poisoned' })}
                      data-testid="status-poisoned-button"
                    >
                      ⚠ Contains Cilantro
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Product Image (optional)</Label>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCamera(true)}
                      style={{ flex: 1 }}
                      data-testid="camera-capture-button"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Take Photo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      style={{ flex: 1 }}
                      data-testid="upload-image-button"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Image
                    </Button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                    data-testid="image-file-input"
                  />
                  {imagePreview && (
                    <div style={{ marginTop: '1rem', position: 'relative' }}>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setImagePreview(null);
                          setNewProduct({ ...newProduct, image_url: '' });
                        }}
                        style={{ marginTop: '0.5rem' }}
                      >
                        Remove Image
                      </Button>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button type="submit" className="flex-1" data-testid="add-product-button">Add Product</Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddForm(false);
                      setNewProduct({ name: '', status: 'safe', image_url: '', barcode: '' });
                      setImagePreview(null);
                    }}
                    data-testid="cancel-add-button"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Scanner;